import { Appear, Card, CardContent, Text, variables } from '@bitrise/bitkit';
import { TypeAttribute, TypeMetricKey } from '@trace/types';
import React, { useContext, FunctionComponent } from 'react';
import { useParams } from 'react-router';
import { ATTRIBUTE_NAME_MAP } from '../../../constants/Attributes';
import { METRIC_UNIT_NAME, METRIC_UNIT_BY_KEY } from '../../../constants/Metrics';
import dataAtIds from '../../../dataAtIds.json';
import useFilters, { TypeFilterLabel } from '../../Filters/useFilters';

import useTrackingEventTimed from '../../Tracking/useTrackingEventTimed';
import { trackEvent } from '../../../utils/tracking';
import { URLStateContext } from '../../URLState/URLState';
import MinMaxChart from '../../ViewVisualisations/Percentile/MinMaxChart/MinMaxChart';
import usePercentileMetric from '../usePercentileMetric';

interface Props {
  animDelay: number;
  attribute: TypeAttribute;
}

const DetailBreakdown: FunctionComponent<Props> = (props) => {
  const { animDelay, attribute } = props;
  const { attributeGroups } = useContext(URLStateContext);
  const { metric } = useParams<{ metric: TypeMetricKey }>() as { metric: TypeMetricKey };
  const { filtersGrouped, isFilterSelected, toggleFilter } = useFilters();
  const metricData = usePercentileMetric(metric, attribute);

  const handlePointerEnter = useTrackingEventTimed('PerformanceDetail.Breakdown.Interaction', {
    attribute,
    metric,
  });

  const handleSeriesClick = (label: TypeFilterLabel) => {
    trackEvent(
      isFilterSelected(label)
        ? 'PerformanceDetail.BreakdownFilter.Removed'
        : 'PerformanceDetail.BreakdownFilter.Added',
      {
        attribute: label.type,
        metric,
        value: label.value,
      },
    );

    toggleFilter(label);
  };

  return (
    <Card
      direction="vertical"
      minWidth="0"
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerEnter.cancel}
    >
      <CardContent direction="vertical" grow padding="x4">
        <Appear animation="FadeSlideDown" delay={animDelay}>
          <Text ellipsis>{ATTRIBUTE_NAME_MAP[attribute]}</Text>
          <Text config="9" ellipsis textColor="gray-5">
            {METRIC_UNIT_NAME[METRIC_UNIT_BY_KEY[metric]]}
          </Text>
        </Appear>
        <MinMaxChart
          metrics={metricData.metrics}
          isFetched={metricData.isFetched}
          isFetching={metricData.isFetching}
          isError={metricData.isError}
          colorScaleBackground={
            attribute === attributeGroups[metric] ? undefined : () => variables.colorOrange1
          }
          colorScaleDiamond={
            attribute === attributeGroups[metric] ? undefined : () => variables.colorOrange2
          }
          data-trace-at-id={dataAtIds.PerformanceDetailBreakdownVisualisation}
          filters={(filtersGrouped.find(([a]) => a === attribute) || [])[1]}
          groupBy={attribute}
          grow
          metric={metric}
          onSeriesClick={(_, value: string) => handleSeriesClick({ type: attribute, value })}
        />
      </CardContent>
    </Card>
  );
};

export default DetailBreakdown;
